import { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  CircularProgress,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import Header from "../../../../components/Header";
import { AssetFinanceUrl } from "../../../../config";
import { executeDynamicSubmit, fetchJsonDynamic } from "../../../../data/DynamicServiceNew/jsondynamicservice";
import Swal from "sweetalert2";

const base_url = AssetFinanceUrl.uri;

const Form = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const [registries, setRegistries] = useState([]);
  const [loadingRegistries, setLoadingRegistries] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetchJsonDynamic("allRegistries", null, null, base_url);
        const results = response?.results ? JSON.parse(response.results) : [];
        setRegistries(results.filter((r) => r.IsActive !== false));
      } catch (err) {
        console.error("Failed to fetch registries:", err);
      } finally {
        setLoadingRegistries(false);
      }
    })();
  }, []);

  const initialValues = {
    code: props.data ? props.data.Code : "",
    displayName: props.data ? props.data.DisplayName : "",
    description: props.data ? props.data.Description : "",
    registryId: props.data ? props.data.RegistryId : "",
    chargeType: props.data ? props.data.ChargeType : "",
    defaultValidityMonths: props.data ? props.data.DefaultValidityMonths : "",
    displayOrder: props.data ? props.data.DisplayOrder : "",
    isActive: props.data ? !!props.data.IsActive : true,
    registryId: props.data ? props.data.RegistryId : "",
  };

  const validationSchema = yup.object().shape({
    code: yup.string().required("Code is required"),
    displayName: yup.string().required("Display name is required"),
    description: yup.string().nullable(),
    registryId: yup.number().nullable(),
    chargeType: yup.string().nullable(),
    defaultValidityMonths: yup
      .number()
      .typeError("Enter a valid number of months")
      .nullable(),
    displayOrder: yup.number().typeError("Enter a valid number").nullable(),
    registryId: yup.number().typeError("Registry is required").required("Registry is required"),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
    const selectedRegistry = registries.find(r => r.Id === parseInt(values.registryId));
    const assetType = {
      code: values.code,
      displayName: values.displayName,
      description: values.description || "",
      registryId: values.registryId ? parseInt(values.registryId) : null,
      registry: selectedRegistry ? (selectedRegistry.Code || selectedRegistry.Name) : "",
      chargeType: values.chargeType || "",
      defaultValidityMonths: values.defaultValidityMonths
        ? parseInt(values.defaultValidityMonths)
        : null,
      displayOrder: values.displayOrder ? parseInt(values.displayOrder) : 0,
      isActive: values.isActive,
      companyId: parseInt(decodedToken.CompanyId),
    };

    if (props.isEditing) {
      assetType.id = props.data.Id;
    }

    await executeDynamicSubmit({
      mutationName: props.isEditing ? "updateAssetType" : "createAssetType",
      variables: assetType,
      mutationObjectName: "assetType",
      baseUrl: base_url,
      isEditing: props.isEditing,
      entityName: "Asset Type",
      onSuccess: () => {
        props.onClose();
        props.onAction();
      },
      setSubmitting,
    });
  };

  if (loadingRegistries) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Asset Type" : "Create Asset Type"}
        subtitle={
          props.isEditing
            ? "Edit an Existing Collateral Asset Type"
            : "Create a New Collateral Asset Type"
        }
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box
              display="grid"
              gap="30px"
              gridTemplateColumns="repeat(4, minmax(0, 1fr))"
            >
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Asset Type*"
                placeholder="e.g. VEHICLE"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.code}
                name="code"
                error={touched.code && !!errors.code}
                helperText={touched.code && errors.code}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Display Name*"
                placeholder="e.g. Motor Vehicle"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.displayName}
                name="displayName"
                error={touched.displayName && !!errors.displayName}
                helperText={touched.displayName && errors.displayName}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                variant="filled"
                type="text"
                label="Description"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.description}
                name="description"
                error={touched.description && !!errors.description}
                helperText={touched.description && errors.description}
                sx={{ gridColumn: "span 4" }}
              />
              <TextField
                select
                fullWidth
                variant="filled"
                label="Registry"
                name="registryId"
                value={values.registryId}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.registryId && !!errors.registryId}
                helperText={touched.registryId && errors.registryId}
                SelectProps={{ native: true }}
                sx={{ gridColumn: "span 2" }}
              >
                <option value=""></option>
                {registries.map((r) => (
                  <option key={r.Id} value={r.Id}>
                    {r.Name} ({r.Code})
                  </option>
                ))}
              </TextField>
              <TextField
                fullWidth
                variant="filled"
                type="text"
                label="Charge Type"
                placeholder="e.g. Chattel Mortgage"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.chargeType}
                name="chargeType"
                error={touched.chargeType && !!errors.chargeType}
                helperText={touched.chargeType && errors.chargeType}
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Default Validity (Months)"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.defaultValidityMonths}
                name="defaultValidityMonths"
                error={
                  touched.defaultValidityMonths &&
                  !!errors.defaultValidityMonths
                }
                helperText={
                  touched.defaultValidityMonths &&
                  errors.defaultValidityMonths
                }
                sx={{ gridColumn: "span 2" }}
              />
              <TextField
                fullWidth
                variant="filled"
                type="number"
                label="Display Order"
                onBlur={handleBlur}
                onChange={handleChange}
                value={values.displayOrder}
                name="displayOrder"
                error={touched.displayOrder && !!errors.displayOrder}
                helperText={touched.displayOrder && errors.displayOrder}
                sx={{ gridColumn: "span 2" }}
              />
              <Box sx={{ gridColumn: "span 4", display: "flex", alignItems: "center" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={values.isActive}
                      onChange={(e) => setFieldValue("isActive", e.target.checked)}
                    />
                  }
                  label="Active"
                />
              </Box>
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Submitting..." : "Submit"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default Form;
